/* eslint-disable react/prop-types */
import { useState, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import { useAuth } from "contexts/AuthContext";
import { API_BASE } from "config/api";

const money = (value) =>
  `GHS ${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

function GroupBreakdownCard({ title, color, entries }) {
  const items = Object.entries(entries || {});
  return (
    <Card sx={{ p: 2.5, height: "100%" }}>
      <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
        {title}
      </Typography>
      {items.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          No campaigns raised yet.
        </Typography>
      ) : (
        <Stack spacing={1.25}>
          {items.map(([name, stats]) => (
            <Box
              key={name}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                pb: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box>
                <Typography variant="button" fontWeight={600} display="block">
                  {name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {stats.campaigns} campaign{stats.campaigns === 1 ? "" : "s"} · Goal{" "}
                  {money(stats.total_goal)}
                </Typography>
              </Box>
              <Chip
                size="small"
                sx={{ bgcolor: `${color}1A`, color }}
                label={money(stats.total_raised)}
              />
            </Box>
          ))}
        </Stack>
      )}
    </Card>
  );
}

function CampaignReportPanel() {
  const { church } = useAuth();
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchReport = useCallback(async () => {
    if (!church?.id) return;
    const token = localStorage.getItem("authToken");
    if (!token) return;
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE}/projects/report`, {
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      setReport(result.data || null);
    } catch (error) {
      console.error("Campaign report fetch failed:", error);
    } finally {
      setIsLoading(false);
    }
  }, [church?.id]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  if (isLoading || !report) return null;

  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12} md={4}>
        <Card sx={{ p: 2.5, height: "100%" }}>
          <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
            General (Church-wide) Campaigns
          </Typography>
          <Typography variant="h4" fontWeight={800}>
            {money(report.general?.total_raised)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {report.general?.campaigns ?? 0} campaign(s) · Goal {money(report.general?.total_goal)}
          </Typography>
          <Box mt={2} pt={2} sx={{ borderTop: "1px solid", borderColor: "divider" }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Raised church-wide (incl. departments &amp; ministries)
            </Typography>
            <Typography variant="h5" fontWeight={800} color="success.main">
              {money(report.grand_total_raised)}
            </Typography>
          </Box>
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <GroupBreakdownCard
          title="Raised by Department"
          color="#0288D1"
          entries={report.by_department}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <GroupBreakdownCard
          title="Raised by Ministry"
          color="#ED6C02"
          entries={report.by_ministry}
        />
      </Grid>
    </Grid>
  );
}

export default CampaignReportPanel;
